import { computed } from 'vue';
import { interpret } from 'xstate';
import { useActor } from '@xstate/vue';

import { CurrentKey, LoanType, RelationshipType } from '@/helpers/const';
import { formatSimpleAddress } from '@/helpers/format';
import {
  CustomerQuoteProcessStep,
  CustomerQuoteProcessStepNames,
  CustomerQuoteProcessSteps,
  StepStatus,
} from '@/types';

import { includesEnum } from './includes-enum';
import {
  initCustomerQuoteMachine,
  loanAppCustomerQuoteMachine,
  type CustomerQuoteMachineContext,
} from './loanCustomerQuoteMachine';

import type { SectionStates, SectionStateType } from '@/types/SectionState';

import type {
  Address,
  Applicant,
  ApplicantId,
  AssetId,
  CustomerQuoteProcessStepEnum,
  CustomerQuoteProcessStepStatus,
  LoanApplication,
  PropertyId,
} from '@/types';

const appKey = localStorage.getItem(CurrentKey) ?? '';
const raw = appKey ? localStorage.getItem(appKey) : null;
const savedState = raw ? JSON.parse(localStorage.getItem(appKey) as string) : undefined;
const initialState: CustomerQuoteMachineContext = savedState ? savedState : initCustomerQuoteMachine();

const service = interpret(loanAppCustomerQuoteMachine.withContext(initialState)).start();

service.onTransition((state) => {
  if (state.value !== 'gotoReady' && state.value !== 'start') {
    const currentStep = state.value as CustomerQuoteProcessStepEnum;
    if (state.context.status[currentStep] === StepStatus.Incomplete) {
      state.context.status[currentStep] = StepStatus.Ready;
    }
    state.context.current = currentStep;
    const loanApp = state.context.loanApp;

    if (loanApp.applicationId && loanApp.userType) {
      const key = `${loanApp.actionType}-${loanApp.applicationId}`;
      const appState = JSON.stringify(state.context);
      localStorage.setItem(CurrentKey, key);
      localStorage.setItem(key, appState);
    }
  }
});

const processSteps = Object.entries(CustomerQuoteProcessSteps).map(([step, info]) => ({
  id: step as CustomerQuoteProcessStepEnum,
  ...info,
}));

export function useCustomerQuoteProcess() {
  const { state, send } = useActor(service);

  const authorisedUser = state.value.context.authorisedUser;

  const currentStep = computed(() => state.value.context.current);
  const loanApp = computed(() => state.value.context.loanApp);
  const sectionStates = computed(() => state.value.context.sectionStates);

  const secondaryApplicant = computed(() => {
    return loanApp.value.actionType === 'updateExisting'
      ? loanApp.value.applicants.find((a) => a.id === state.value.context.authorisedUser.id && a.type === 'secondary')
      : undefined;
  });

  const isSecondaryMarried = computed(
    () => secondaryApplicant.value?.relationshipToPrimary === RelationshipType.MARRIED_OR_DEFACTO,
  );

  const excludedMenus =
    loanApp.value.endDebt >= 0
      ? [CustomerQuoteProcessStep.Income, CustomerQuoteProcessStep.Expenses, CustomerQuoteProcessStep.Assets]
      : [];

  const stepper = computed<CustomerQuoteProcessStepStatus[]>(() => {
    const currentStep = CustomerQuoteProcessStepNames.includes(state.value.context.current)
      ? state.value.context.current
      : CustomerQuoteProcessStepNames[0];

    return processSteps
      .filter(
        (step) =>
          loanApp.value.userType &&
          step.actions.includes(loanApp.value.actionType) &&
          step.id !== CustomerQuoteProcessStep.Finished &&
          ((loanApp.value.endDebt > 0 && !isSecondaryMarried.value) ||
            state.value.context.from === 'crm' ||
            !includesEnum(step.id, excludedMenus) ||
            (loanApp.value.loanType === LoanType.SINGLE_SECURITY && step.id === CustomerQuoteProcessStep.Assets)),
      )
      .map((step) => {
        const stepStatus = state.value.context.status[step.id];
        const status =
          stepStatus === StepStatus.Incomplete &&
          CustomerQuoteProcessSteps[step.id].order < CustomerQuoteProcessSteps[currentStep].order
            ? StepStatus.Skipped
            : stepStatus;
        return { ...step, status };
      })
      .filter((step) => step.status !== StepStatus.Skipped);
  });

  const loanProperties = computed(() =>
    [...loanApp.value.incomingProperties, ...loanApp.value.outgoingProperties].reduce((obj, item) => {
      obj[item.id] = formatSimpleAddress(item.address as Address);
      return obj;
    }, {} as Record<PropertyId | AssetId, string>),
  );

  const loanApplicants = computed<Record<string, Applicant>>(() =>
    loanApp.value.applicants.reduce((obj, item) => {
      obj[item.id as string] = item;
      return obj;
    }, {} as Record<ApplicantId, Applicant>),
  );

  const saveAndGoNext = (updateData: Partial<LoanApplication> = {}) => {
    if (currentStep.value === CustomerQuoteProcessStep.SubmitCustomerQuote) {
      localStorage.setItem(CurrentKey, '');
    }
    send({ type: 'NEXT', loanApp: { ...state.value.context.loanApp, ...updateData } });
  };

  const goBack = () => {
    send('BACK');
  };

  const restart = () => {
    send('RESTART');
  };

  const updateSpecificSectionState = (sectionKey: keyof SectionStates, value: SectionStateType) => {
    send({ type: 'UPDATE_SECTION_STATE', sectionKey, value });
  };

  const updateAllSectionStates = (value: SectionStateType) => {
    send({ type: 'UPDATE_ALL_SECTION_STATES', value });
  };

  return {
    state,
    service,
    stepper,
    currentStep,
    authorisedUser,
    loanApp,
    loanProperties,
    loanApplicants,
    sectionStates,
    send,
    saveAndGoNext,
    goBack,
    restart,
    updateSpecificSectionState,
    updateAllSectionStates,
  };
}
